import React, { useState } from "react";
import { MenuItem, Select } from "@mui/material";
import { useGetDmsSetupsByDmsDealerIdMutation } from "../../redux/reducer/features/Integration/DmsSetupSlice";
import { AppState } from "../../redux/reducer/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "@reduxjs/toolkit";
import { SwitchSetupApi } from "../../redux/reducer/features/Auth/authSlice";
import SnackBarPopup from "../app/SnackBarPopup";
import { setupSwitchService } from "../../services/setup/setupSwitchService";

export const SetupSelectorDropdown = () => {
  const dispatch = useDispatch<Dispatch<any>>();

  const { targetWebAccessToken, dmsDealerId, setupId, userId } = useSelector(
    (state: AppState) => state.authReducer,
  );

  const [showSetupChangeAlert, setShowSetupChangeAlert] = React.useState(false);
  const [setupChangeAlertMessage, setSetupChangeAlertMessage] =
    React.useState("");
  const [setupChangeAlertSeverity, setSetupChangeAlertSeverity] =
    React.useState<"success" | "error">("success");
  const [dealerSetups, setDealerSetups] = useState<any[]>([]);
  const [selectedSetupId, setSelectedSetupId] = useState<string>(setupId ?? "");
  const [isSwitching, setIsSwitching] = useState<boolean>(false);

  const [getDmsSetupsMutation, getDmsSetupsMutationResult] =
    useGetDmsSetupsByDmsDealerIdMutation();

  React.useEffect(() => {
    if (dmsDealerId) {
      getDmsSetupsMutation({ dmsDealerId: dmsDealerId ?? "" });
    }
  }, [dmsDealerId]);

  React.useEffect(() => {
    if (
      getDmsSetupsMutationResult.isSuccess &&
      getDmsSetupsMutationResult.data
    ) {
      setDealerSetups(getDmsSetupsMutationResult.data?.entityList ?? []);
    }
  }, [getDmsSetupsMutationResult]);

  React.useEffect(() => {
    if (setupId && setupId !== selectedSetupId && !isSwitching) {
      setSelectedSetupId(setupId);
    }
  }, [setupId, selectedSetupId, isSwitching]);

  React.useEffect(() => {
    const msg = setupSwitchService.getMessage();
    if (msg) {
      setShowSetupChangeAlert(true);
      setSetupChangeAlertMessage(msg);
      setSetupChangeAlertSeverity("success");
      setupSwitchService.clear();
    }
  }, []);

  const onComplete = () => {
    setIsSwitching(false);
    setupSwitchService.setMessage("Setup switched successfully!");
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const onError = (errorMessage?: string) => {
    setIsSwitching(false);
    setSelectedSetupId(setupId ?? "");
    const message = errorMessage || "Failed to switch setup. Please try again.";
    setSetupChangeAlertMessage(message);
    setSetupChangeAlertSeverity("error");
    setShowSetupChangeAlert(true);
  };

  const switchSetup = async (newSetupId: string) => {
    if (isSwitching) {
      return;
    }

    setIsSwitching(true);

    try {
      const request = {
        dmsSetupId: newSetupId,
        accessToken: targetWebAccessToken ?? "",
        userId: userId ?? "",
      };
      dispatch(SwitchSetupApi(request, onComplete, onError));
    } catch (error) {
      console.error("Error during setup switch:", error);
      onError("An unexpected error occurred during setup switch.");
    }
  };

  const handleSetupChange = async (e: any) => {
    const newSetupId = e.target.value as string;

    if (isSwitching || newSetupId === setupId) {
      return;
    }

    setSelectedSetupId(newSetupId);
    await switchSetup(newSetupId);
  };

  const isDropdownDisabled =
    getDmsSetupsMutationResult.isLoading || isSwitching || !dealerSetups.length;

  return (
    <>
      <Select
        sx={{
          width: "350px",
          height: 46,
          fontSize: "1.4rem",
          fontWeight: 600,
          opacity: isSwitching ? 0.7 : 1,
          cursor: isDropdownDisabled ? "not-allowed" : "pointer",
        }}
        value={selectedSetupId}
        onChange={handleSetupChange}
        variant="outlined"
        disabled={isDropdownDisabled}
        displayEmpty
      >
        {getDmsSetupsMutationResult.isLoading ? (
          <MenuItem value="">Loading setups...</MenuItem>
        ) : dealerSetups.length === 0 ? (
          <MenuItem value="">No setups available</MenuItem>
        ) : (
          dealerSetups.map((setup: any) => (
            <MenuItem key={setup.id} value={setup.id}>
              {setup.siteName || `Setup ${setup.id}`}
            </MenuItem>
          ))
        )}
      </Select>

      <SnackBarPopup
        severity={setupChangeAlertSeverity}
        message={setupChangeAlertMessage}
        onClose={() => setShowSetupChangeAlert(false)}
        open={showSetupChangeAlert}
        autoHideDuration={setupChangeAlertSeverity === "success" ? 5000 : 8000}
      />
    </>
  );
};
